import React, {Fragment, useEffect, useRef} from 'react';
import Input from 'patient-ping-remedy/packages/input';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import DatePicker from 'patient-ping-remedy/packages/date-picker';
import {sizes} from 'patient-ping-remedy/packages/theme';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {DatePickerWrapper} from '@common/DatePickerWrapper.js';
import {GenderWrapper, StyledRow} from '@manual_encounters/encounter_client_search/PatientInformationForm.styles';
import DisplayHelpers from '@/helpers/display_helpers';
import {
  genderOptions,
  ethnicityOptions,
  languageOptions,
  maritalStatusOptions,
  raceOptions,
  veteranOptions,
  phoneTypeOptions,
} from '@manual_encounters/constants/ExtendedDemographicsOptions';
import Banner, {BannerTypes} from "patient-ping-remedy/packages/banner";
import helpers from "@/helpers/helpers.js";
import {DemographicFieldRequirements} from "@/api/dto/state-config";
import {GenericObject} from "@/hooks/useForm";

const requiredField = 'Required field';
const invalidValue = 'Invalid value';

const hasAddressValue = (value: unknown) => typeof value === 'string' && !helpers.isEmpty(value.trim());

type Props = {
  form: GenericObject;
  handleChange: (e: React.ChangeEvent<HTMLInputElement> | null, name: string, value: any) => void;
  makeFieldRequired: (name: string, required: boolean) => void;
  disabled?: boolean;
  showAll?: boolean;
  isWithinModal?: boolean;
  focusOnLoad?: boolean;
  featureName?: string;
  modalType?: string;
  isReferralModal?: boolean;
  demographicFieldRequirements?: DemographicFieldRequirements;
}

export default function PatientDemographicsFormNew({
  form,
  handleChange,
  makeFieldRequired,
  disabled = false,
  showAll = true,
  isWithinModal = true,
  focusOnLoad = true,
  featureName = 'PatientAdmit',
  modalType = 'patient-admit',
  isReferralModal = false,
  demographicFieldRequirements,
}: Props) {
  const firstNameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (focusOnLoad) {
      firstNameRef.current?.focus();
    }
  }, []);

  const age = form.dob.value ? helpers.getAge(form.dob.value) : null;
  const isMinor = typeof age === 'number' && age < 18;
  const hasPhone = typeof form.phone.value === 'string' && /\d/.test(form.phone.value);
  // Entering any part of an address in the admit modal requires the rest of it, except Address 2.
  const requiresCompleteAddress = !isReferralModal
    && [form.address1.value, form.city.value, form.state.value, form.zip.value].some(hasAddressValue);
  const isAddress1Required = requiresCompleteAddress || !!demographicFieldRequirements?.address1;
  const isCityRequired = requiresCompleteAddress || !!demographicFieldRequirements?.city;
  const isStateRequired = requiresCompleteAddress || !!demographicFieldRequirements?.state;
  const isZipRequired = requiresCompleteAddress || !!demographicFieldRequirements?.zip;

  useEffect(() => {
    makeFieldRequired('address1', isAddress1Required);
    makeFieldRequired('city', isCityRequired);
    makeFieldRequired('state', isStateRequired);
    makeFieldRequired('zip', isZipRequired);
  }, [isAddress1Required, isCityRequired, isStateRequired, isZipRequired]);

  useEffect(() => {
    if (!hasPhone) {
      handleChange(null, 'phoneType', null);
    }
  }, [hasPhone]);

  const isSSNDisabled = (disabled && form.ssn.value && !form.ssn.touched && !modalType);
  const isClientIdDisabled = (disabled && form.clientId.value && !form.clientId.touched && !modalType);
  const isZipDisabled = (disabled && form.zip.value && !form.zip.touched && !modalType);
  const isPhoneDisabled = (disabled && form.phone.value && !form.phone.touched && !modalType);
  const isPhoneTypeDisabled = (disabled && form.phoneType.value && !form.phoneType.touched && !modalType);
  const isAddress1Disabled = (disabled && form.address1.value && !form.address1.touched && !modalType);
  const isAddress2Disabled = (disabled && form.address2.value && !form.address2.touched && !modalType);
  const isStateDisabled = (disabled && form.state.value && !form.state.touched && !modalType);
  const isCityDisabled = (disabled && form.city.value && !form.city.touched && !modalType);
  const isEthnicityDisabled = (disabled && form.ethnicity.value && !form.ethnicity.touched && !modalType);
  const isVeteranStatusDisabled = (disabled && form.veteranStatus.value && !form.veteranStatus.touched && !modalType);
  const isLanguageDisabled = (disabled && form.language.value && !form.language.touched && !modalType);
  const isRaceDisabled = (disabled && form.race.value && !form.race.touched && !modalType);
  const isMaritalStatusDisabled = (disabled && form.maritalStatus.value && !form.maritalStatus.touched && !modalType);

  return (
    <Fragment>
      <StyledRow>
        <Input
          name="firstName"
          labelText="First Name"
          id="firstName"
          value={form.firstName.value ?? ''}
          error={form.firstName.touched && !form.firstName.valid.valid}
          errorMessage={form.firstName.valid.message}
          required="required"
          isDisabled={disabled}
          onChange={(e) => handleChange(e, 'firstName', e.target.value)}
          ref={(input) => {
            firstNameRef.current = input;
          }}
        />
        <Spacer itemWidth={sizes.large} />
        <Input
          name="lastName"
          labelText="Last Name"
          id="lastName"
          value={form.lastName.value ?? ''}
          error={form.lastName.touched && !form.lastName.valid.valid}
          errorMessage={form.lastName.valid.message}
          required="required"
          isDisabled={disabled}
          onChange={(e) => handleChange(e, 'lastName', e.target.value)}
        />
      </StyledRow>
      <StyledRow>
        <Input
          name="clientId"
          id="clientId"
          type="text"
          labelText="Client ID"
          required="optional"
          autoFocus={focusOnLoad}
          isDisabled={isClientIdDisabled}
          placeholder="Enter id"
          value={form.clientId.value ?? ''}
          onChange={(e) => handleChange(e, 'clientId', e.target.value)}
        />
      </StyledRow>

      {showAll && (
        <Fragment>
          <StyledRow>
            <GenderWrapper>
              <DropdownList
                handleChange={(newValue: Value | null) => handleChange(null, 'gender', newValue?.value)}
                value={genderOptions.find((g) => g.value === form.gender.value)}
                id="gender"
                items={genderOptions}
                label="Gender"
                disabled={disabled}
                isWithinModal={isWithinModal}
                required
                error={form.gender.touched && !form.gender.valid.valid}
                errorMessage={requiredField}
              />
            </GenderWrapper>
            <DatePickerWrapper>
              <DatePicker
                value={form.dob.value ? new Date(form.dob.value) : null}
                onChange={(date) => {
                  handleChange(null, 'dob', date);
                  return date;
                }}
                disabled={disabled}
                maxDate={new Date()}
                externalError={form.dob.touched && !form.dob.valid.valid}
                externalErrorText={requiredField}
                id={`dob${featureName}`}
                labelText="Date of Birth"
                required="required"
              />
            </DatePickerWrapper>
          </StyledRow>
          <StyledRow>
            {/* UE-3050 Only validate if field is enabled */}
            <Input
              name="ssn"
              id="ssn"
              type="text"
              labelText="SSN - Last Four"
              required={demographicFieldRequirements?.ssn ? "required" : "optional"}
              isDisabled={isSSNDisabled}
              value={form.ssn.value ?? ''}
              error={form.ssn.touched && !form.ssn.valid.valid}
              errorMessage={form.ssn.value ? invalidValue : requiredField}
              placeholder="####"
              maxLength={4}
              onChange={(e) => handleChange(e, 'ssn', e.target.value)}
            />
            {isMinor && (
              <Banner type={BannerTypes.WARNING}>
                Client is under the age of 18
              </Banner>
            )}
          </StyledRow>
          <Input
            name="address1"
            id="address1"
            type="text"
            labelText="Address 1"
            required={isAddress1Required ? "required" : "optional"}
            isDisabled={isAddress1Disabled}
            value={form.address1.value ?? ''}
            error={form.address1.touched && !form.address1.valid.valid}
            errorMessage={form.address1.value ? invalidValue : requiredField}
            maxLength={100}
            onChange={(e) => handleChange(e, 'address1', e.target.value)}
          />
          <Spacer itemWidth={sizes.large}/>
          <Input
            name="address2"
            id="address2"
            type="text"
            labelText="Address 2"
            required={demographicFieldRequirements?.address2 ? "required" : "optional"}
            isDisabled={isAddress2Disabled}
            value={form.address2.value ?? ''}
            error={form.address2.touched && !form.address2.valid.valid}
            errorMessage={form.address2.value ? invalidValue : requiredField}
            maxLength={100}
            onChange={(e) => handleChange(e, 'address2', e.target.value)}
          />
          <StyledRow>
            <Input
              name="city"
              id="city"
              type="text"
              labelText="City"
              required={isCityRequired ? "required" : "optional"}
              isDisabled={isCityDisabled}
              value={form.city.value ?? ''}
              error={form.city.touched && !form.city.valid.valid}
              errorMessage={form.city.value ? invalidValue : requiredField}
              maxLength={50}
              onChange={(e) => handleChange(e, 'city', e.target.value)}
            />
            <Input
              name="state"
              id="state"
              type="text"
              labelText="State"
              required={isStateRequired ? "required" : "optional"}
              isDisabled={isStateDisabled}
              value={form.state.value ?? ''}
              error={form.state.touched && !form.state.valid.valid}
              errorMessage={form.state.value ? invalidValue : requiredField}
              maxLength={2}
              onChange={(e) => handleChange(e, 'state', e.target.value)}
            />
          </StyledRow>
          <StyledRow>
            <Input
              name="zip"
              id="zip"
              type="text"
              labelText="Zip Code"
              required={isZipRequired ? "required" : "optional"}
              isDisabled={isZipDisabled}
              value={form.zip.value ?? ''}
              error={form.zip.touched && !form.zip.valid.valid}
              errorMessage={form.zip.value ? invalidValue : requiredField}
              maxLength={5}
              onChange={(e) => handleChange(e, 'zip', e.target.value)}
            />
            <DropdownList
              handleChange={(newValue: Value | null) => handleChange(null, 'language', newValue?.value)}
              isClearable={!demographicFieldRequirements?.language}
              disabled={isLanguageDisabled}
              value={languageOptions.find((language) => language.value === form.language.value)}
              id="language"
              items={languageOptions}
              label="Language"
              labelMessage={demographicFieldRequirements?.language ? undefined : "optional"}
              required={!!demographicFieldRequirements?.language}
              error={form.language.touched && !form.language.valid.valid}
              errorMessage={requiredField}
            />
          </StyledRow>
          <StyledRow>
            <Input
              name="phone"
              id="phone"
              type="text"
              labelText="Phone Number"
              required={demographicFieldRequirements?.phone ? "required" : "optional"}
              value={DisplayHelpers.formatPhoneNumber(form.phone.value)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const raw = e.target.value.replace(/\D/g, '').substring(0, 10);
                handleChange(e, 'phone', raw);
              }}
              placeholder="(___) ___-____"
              isDisabled={isPhoneDisabled}
              error={form.phone.touched && !form.phone.valid.valid}
              errorMessage={form.phone.value ? invalidValue : requiredField}
            />
            {hasPhone && (
              <>
                <Spacer itemWidth={sizes.large}/>
                <DropdownList
                  handleChange={(newValue: Value | null) => handleChange(null, 'phoneType', newValue?.value)}
                  isClearable={false}
                  disabled={isPhoneTypeDisabled}
                  value={phoneTypeOptions.find((phoneType) => phoneType.value === form.phoneType.value)}
                  id="phoneType"
                  items={phoneTypeOptions}
                  label="Phone Type"
                  labelMessage={undefined}
                  required={true}
                  error={form.phoneType.touched && !form.phoneType.valid.valid}
                  errorMessage={requiredField}
                />
              </>
            )}
          </StyledRow>
          <StyledRow>
            <DropdownList
              handleChange={(newValue: Value | null) => handleChange(null, 'maritalStatus', newValue?.value)}
              isClearable={!demographicFieldRequirements?.maritalStatus}
              disabled={isMaritalStatusDisabled}
              value={maritalStatusOptions.find((maritalStatus) => maritalStatus.value === form.maritalStatus.value)}
              id="maritalStatus"
              items={maritalStatusOptions}
              label="Marital Status"
              labelMessage={demographicFieldRequirements?.maritalStatus ? undefined : "optional"}
              required={!!demographicFieldRequirements?.maritalStatus}
              error={form.maritalStatus.touched && !form.maritalStatus.valid.valid}
              errorMessage={requiredField}
            />
            <DropdownList
              handleChange={(newValue: Value | null) => handleChange(null, 'veteranStatus', newValue?.value)}
              isClearable={!demographicFieldRequirements?.veteranStatus}
              disabled={isVeteranStatusDisabled}
              value={veteranOptions.find((veteranStatus) => veteranStatus.value === form.veteranStatus.value)}
              id="veteranStatus"
              items={veteranOptions}
              label="Veteran Status"
              labelMessage={demographicFieldRequirements?.veteranStatus ? undefined : "optional"}
              required={!!demographicFieldRequirements?.veteranStatus}
              error={form.veteranStatus.touched && !form.veteranStatus.valid.valid}
              errorMessage={requiredField}
            />
          </StyledRow>
          <StyledRow>
            <DropdownList
              handleChange={(newValue: Value | null) => handleChange(null, 'race', newValue?.value)}
              isClearable={!demographicFieldRequirements?.race}
              disabled={isRaceDisabled}
              value={raceOptions.find((race) => race.value === form.race.value)}
              id="race"
              items={raceOptions}
              label="Race"
              labelMessage={demographicFieldRequirements?.race ? undefined : "optional"}
              required={!!demographicFieldRequirements?.race}
              error={form.race.touched && !form.race.valid.valid}
              errorMessage={requiredField}
            />
            <DropdownList
              handleChange={(newValue: Value | null) => handleChange(null, 'ethnicity', newValue?.value)}
              isClearable={!demographicFieldRequirements?.ethnicity}
              disabled={isEthnicityDisabled}
              value={ethnicityOptions.find((ethnicity) => ethnicity.value === form.ethnicity.value)}
              id="ethnicity"
              items={ethnicityOptions}
              label="Ethnicity"
              labelMessage={demographicFieldRequirements?.ethnicity ? undefined : "optional"}
              required={!!demographicFieldRequirements?.ethnicity}
              error={form.ethnicity.touched && !form.ethnicity.valid.valid}
              errorMessage={requiredField}
            />
          </StyledRow>
        </Fragment>
      )}
    </Fragment>
  );
}
